import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Printer, CheckCircle, Download, Volume2, VolumeX, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export default function PrintMonitor() {
  const { templateId } = useParams();
  const [template, setTemplate] = useState(null);
  const [lastPrintedCount, setLastPrintedCount] = useState(0);
  const [printQueue, setPrintQueue] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [monitoring, setMonitoring] = useState(true);
  const [newUploadDetected, setNewUploadDetected] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchTemplate();
    
    // Poll for new uploads every 2 seconds
    const interval = setInterval(() => {
      if (monitoring) {
        checkForNewUploads();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [templateId, monitoring, lastPrintedCount]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`);
      setTemplate(response.data);
      setLastPrintedCount(response.data.filled_count);
    } catch (error) {
      console.error("Failed to fetch template");
    }
  };

  const playNotificationSound = () => {
    if (soundEnabled) {
      // Create a simple beep sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => oscillator.stop(), 200);
    }
  };

  const checkForNewUploads = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`);
      const newCount = response.data.filled_count;
      
      if (newCount > lastPrintedCount) {
        // New upload detected!
        playNotificationSound();
        setNewUploadDetected(true);
        
        // Add to print queue
        setPrintQueue(prev => [...prev, {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString()
        }]);
        
        setLastPrintedCount(newCount);
        setTemplate(response.data);
        
        // Auto-download PDF
        downloadPDF();
      }
    } catch (error) {
      console.error("Failed to check for uploads");
    }
  };

  const downloadPDF = () => {
    // Create a direct download link
    const pdfUrl = `${API}/templates/${templateId}/pdf`;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `photo_${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => setNewUploadDetected(false), 3000);
  };

  const openPDFInNewTab = () => {
    const pdfUrl = `${API}/templates/${templateId}/pdf`;
    window.open(pdfUrl, '_blank');
  };

  if (!template) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center">
            <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-all ${newUploadDetected ? 'bg-green-500 animate-pulse scale-110' : 'bg-gradient-to-br from-green-500 to-blue-600'}`}>
              <Printer className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {newUploadDetected ? '🎉 New Photo!' : 'Print Monitor'}
            </CardTitle>
            <p className="text-gray-600">Template: {template.name}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status */}
            <div className={`p-4 rounded-lg ${monitoring ? 'bg-green-100' : 'bg-gray-100'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${monitoring ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                  <span className="font-medium">
                    {monitoring ? 'Watching for uploads...' : 'Paused'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSoundEnabled(!soundEnabled)}
                  >
                    {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMonitoring(!monitoring)}
                  >
                    {monitoring ? 'Pause' : 'Resume'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-blue-600">{template.filled_count}</p>
                <p className="text-sm text-gray-600">Current Photo</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-purple-600">{printQueue.length}</p>
                <p className="text-sm text-gray-600">Total Printed</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={downloadPDF}
                className="bg-gradient-to-r from-green-600 to-green-700"
                disabled={template.filled_count === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={openPDFInNewTab}
                variant="outline"
                disabled={template.filled_count === 0}
              >
                <Printer className="w-4 h-4 mr-2" />
                Open & Print
              </Button>
            </div>

            <Button
              onClick={fetchTemplate}
              variant="outline"
              className="w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Status
            </Button>

            {/* Print History */}
            {printQueue.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-gray-700">Print History</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {printQueue.slice(-10).reverse().map((job) => (
                    <div key={job.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Photo uploaded & downloaded</p>
                        <p className="text-xs text-gray-500">{job.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* QR Code Link */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">📱 Share Upload Link</h3>
              <p className="text-xs text-blue-700 break-all bg-white p-2 rounded">
                {process.env.REACT_APP_FRONTEND_URL || window.location.origin}/upload/{templateId}
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="font-medium text-yellow-800 mb-2">📋 How to use</h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>1. Keep this page open on your PC</li>
                <li>2. Share the upload link with guests</li>
                <li>3. When a photo is uploaded, PDF auto-downloads</li>
                <li>4. Open the downloaded PDF and print (Ctrl+P)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
